import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { saveAs } from 'file-saver';
import {
  Field,
  FieldType,
  MemberAddeditComponent,
  MemberAddeditDialogModel,
} from '../../brokeragency/member-addedit/member-addedit.component';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import {
  InboundErrorListQuery,
  InboundFileFeed,
  InboundFileFeedCapabilities,
  InboundFileFeedsService,
  InboundFileFeedHandlerFormat,
  InboundFileFeedHandlerFormatField,
  InboundFileFeedScopeOption,
  InboundFileFeedScopeOptions,
  InboundFileImport,
  InboundSftpUpload,
  InboundSftpFilePage,
  InboundFileImportError,
  InboundFileUnmatchedMessage,
  InboundFileFeedTransportPollStatus,
  InboundFeedListQuery,
  InboundFeedScopeType,
  InboundUnmatchedMessageListQuery,
} from '../../../services/inbound-file-feeds/inbound-file-feeds.service';

interface FeedDialogValue {
  feedKey?: string;
  name: string;
  description?: string;
  status: string;
  scopeOption: string;
  handlerType: string;
  emailEnabled?: boolean;
  emailSubjectTokens?: string;
  emailAllowedSenders?: string;
  emailRequireAttachment?: boolean;
  emailRequireDkimPass?: boolean;
  emailRequireSpfPass?: boolean;
  emailRequirePgp?: boolean;
  sftpEnabled?: boolean;
  sftpUsername?: string;
  sftpFolder?: string;
  sftpRequirePgp?: boolean;
  sftpProcessingMode?: 'daily' | 'upload';
  parserConfig?: Record<string, any>;
  validationConfig?: Record<string, any>;
  notificationConfig?: Record<string, any>;
  metadata?: Record<string, any>;
}

interface ErrorStatusDialogValue {
  status: string;
  resolutionNote?: string;
}

interface HandlerFormatViewModel {
  handlerType: string;
  format: InboundFileFeedHandlerFormat;
  requiredFields: InboundFileFeedHandlerFormatField[];
  optionalFields: InboundFileFeedHandlerFormatField[];
  downloadTypes: string[];
}

interface FeedSelectionOptions {
  selectedTabIndex?: number;
  showToast?: boolean;
}

type JsonLike = Record<string, any>;

const HANDLER_LABELS: Record<string, string> = {
  vendor_status: 'Vendor status',
  team_member_upload: 'Team member upload',
  team_reward_topup: 'Team reward/top-up',
  broker_census: 'Broker census',
  broker_eligibility: 'Broker eligibility',
  broker_enrollment: 'Broker enrollment',
  marketing_group_email_members: 'Marketing group email members',
  marketing_sequence_enrollment: 'Marketing sequence enrollment',
  user_brand_migration: 'User brand migration',
  generic_file: 'Generic file',
};

const SCOPELESS_HANDLER_TYPES = new Set<string>([
  'marketing_sequence_enrollment',
]);

const SCOPE_LABELS: Record<string, string> = {
  vendor: 'Vendor',
  team: 'Team',
  company: 'Company',
  client: 'Client',
  vendorProduct: 'Vendor product',
  marketingGroup: 'Marketing group',
  partner: 'Partner',
};

@Component({
  selector: 'app-inbound-file-feeds',
  templateUrl: './inbound-file-feeds.component.html',
  styleUrls: ['./inbound-file-feeds.component.scss'],
})
export class InboundFileFeedsComponent implements OnInit, OnDestroy {
  @ViewChild('filesPanel') filesPanel!: TemplateRef<unknown>;
  protected filesFeed: InboundFileFeed | null = null;
  protected filePage: InboundSftpFilePage | null = null;
  protected filesLoading = false;
  protected filesError = '';
  protected filesBusy = false;
  protected fileAction: 'move' | 'rename' | 'delete' | null = null;
  protected actionFile: InboundSftpFilePage['files'][number] | null = null;
  protected fileDestination = '';
  protected filesSort: Sort = { active: 'name', direction: 'asc' };
  private filesGeneration = 0;
  private filesDialog: MatDialogRef<unknown> | null = null;

  @ViewChild('sftpPanel') sftpPanel!: TemplateRef<unknown>;
  protected sftpFeed: InboundFileFeed | null = null;
  protected sftpSetupFeed: InboundFileFeed | null = null;
  protected sftpBusy = false;
  protected sftpStatuses: Record<string, string> = {};
  private sftpDialog: MatDialogRef<unknown> | null = null;

  @ViewChild('feedsSort') feedsSort!: MatSort;
  @ViewChild('unmatchedMessagesSort') unmatchedMessagesSort!: MatSort;
  @ViewChild('importsSort') importsSort!: MatSort;
  @ViewChild('errorsSort') errorsSort!: MatSort;

  protected capabilities: InboundFileFeedCapabilities | null = null;
  protected scopeOptions: InboundFileFeedScopeOptions = this.emptyScopeOptions();
  protected feeds = new MatTableDataSource<InboundFileFeed>();
  protected unmatchedMessages = new MatTableDataSource<InboundFileUnmatchedMessage>();
  protected imports = new MatTableDataSource<InboundFileImport>();
  protected errors = new MatTableDataSource<InboundFileImportError>();
  protected selectedFeed: InboundFileFeed | null = null;
  protected selectedUnmatchedMessage: InboundFileUnmatchedMessage | null = null;
  protected selectedImport: InboundFileImport | null = null;
  protected loadingFeeds = false;
  protected loadingUnmatchedMessages = false;
  protected loadingDetails = false;
  protected loadingScopes = false;
  protected loadingPollStatus = false;
  protected pollStatusLoaded = false;
  protected pollStatusRefreshFailed = false;
  protected runningPollNow = false;
  protected scanningUploadedFiles = false;
  protected scopeSearch = '';
  protected scopeOptionType: InboundFeedScopeType = 'team';
  protected emailPollStatus: InboundFileFeedTransportPollStatus | null = null;
  protected pollStatusNow = Date.now();
  protected handlerFormatItems: HandlerFormatViewModel[] = [];
  protected filters: InboundFeedListQuery = {
    status: 'active',
    scopeType: 'all',
    transport: 'all',
    handlerType: 'all',
    filter: '',
    limit: 100,
    offset: 0,
  };
  protected unmatchedMessageFilters: InboundUnmatchedMessageListQuery = {
    status: 'open',
    transport: 'email',
    filter: '',
    limit: 100,
    offset: 0,
  };
  protected importFilters = {
    status: 'all',
  };
  protected errorFilters: InboundErrorListQuery = {
    status: 'open',
    severity: 'all',
  };
  protected selectedTabIndex = 0;

  protected feedColumns = ['name', 'status', 'scope', 'handlerType', 'transports', 'lastImportStatus', 'lastImportAt', 'updatedAt', 'actions'];
  protected unmatchedMessageColumns = ['createdAt', 'transport', 'sourceIdentifier', 'subject', 'originalFileName', 'errorCode', 'message', 'status', 'actions'];
  protected sftpUploads: InboundSftpUpload[] = [];
  protected loadingSftpUploads = false;
  protected retryingUpload: string | null = null;
  protected sftpUploadsError = '';
  protected importColumns = ['status', 'transport', 'originalFileName', 'sourceIdentifier', 'counts', 'receivedAt', 'finishedAt', 'actions'];
  protected errorColumns = ['rowNumber', 'fieldName', 'severity', 'status', 'message', 'resolutionNote', 'actions'];
  protected readonly fieldType = FieldType;
  protected readonly tabIndexes = {
    feeds: 0,
    unmatchedMessages: 1,
    handlerFormats: 2,
    imports: 3,
    errors: 4,
  };
  private scopeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private scopeOptionsRequestId = 0;
  private pollStatusRefreshHandle: ReturnType<typeof setTimeout> | null = null;
  private pollStatusTickHandle: ReturnType<typeof setInterval> | null = null;
  private lastCompletedPollKey: string | null = null;

  constructor(
    private inboundFileFeedsService: InboundFileFeedsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private uiAlertService: UiAlertService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.startPollStatusClock();
    await Promise.all([
      this.loadCapabilities(),
      this.loadPollStatus(),
      this.loadUnmatchedMessages(),
    ]);
    await this.refreshScopeOptions();
    await this.loadFeeds();
  }

  ngOnDestroy(): void {
    this.filesGeneration++;
    this.filesDialog?.close();
    this.sftpDialog?.close();
    if (this.scopeSearchDebounceHandle) {
      clearTimeout(this.scopeSearchDebounceHandle);
      this.scopeSearchDebounceHandle = null;
    }
    if (this.pollStatusRefreshHandle) {
      clearTimeout(this.pollStatusRefreshHandle);
      this.pollStatusRefreshHandle = null;
    }
    if (this.pollStatusTickHandle) {
      clearInterval(this.pollStatusTickHandle);
      this.pollStatusTickHandle = null;
    }
  }

  protected async loadCapabilities(): Promise<void> {
    try {
      this.capabilities = await this.inboundFileFeedsService.capabilities();
      this.handlerFormatItems = this.buildHandlerFormatItems();
    } catch (error) {
      console.error('Unable to initialize inbound feed capabilities', error);
      this.handlerFormatItems = [];
      this.snackBar.open('Unable to load inbound feed capabilities.', 'Close', { duration: 3000 });
    }
  }

  protected async loadPollStatus(showErrorSnackbar = true): Promise<void> {
    this.loadingPollStatus = true;
    try {
      const previousCompletedPollKey = this.lastCompletedPollKey;
      const pollStatus = await this.inboundFileFeedsService.pollStatus();
      this.emailPollStatus = pollStatus?.email || null;
      this.lastCompletedPollKey = this.getCompletedPollKey(this.emailPollStatus);
      this.pollStatusRefreshFailed = false;

      if (previousCompletedPollKey && this.lastCompletedPollKey && previousCompletedPollKey !== this.lastCompletedPollKey) {
        await this.refreshPollResultViews();
      }
    } catch (error) {
      console.error('Unable to load inbound feed poll status', error);
      this.pollStatusRefreshFailed = true;
      if (showErrorSnackbar && !this.emailPollStatus) {
        this.snackBar.open('Unable to load inbound email poll status.', 'Close', { duration: 3000 });
      }
    } finally {
      this.loadingPollStatus = false;
      this.pollStatusLoaded = true;
      this.schedulePollStatusRefresh();
    }
  }

  protected async onScopeOptionTypeChange(): Promise<void> {
    await this.refreshScopeOptions();
  }

  protected onScopeSearchChange(): void {
    if (this.scopeSearchDebounceHandle) {
      clearTimeout(this.scopeSearchDebounceHandle);
    }

    this.scopeSearchDebounceHandle = setTimeout(() => {
      this.scopeSearchDebounceHandle = null;
      void this.refreshScopeOptions();
    }, 300);
  }

  protected async loadSftpUploads(): Promise<void> {
    this.loadingSftpUploads = true;
    this.sftpUploadsError = '';
    try { this.sftpUploads = await this.inboundFileFeedsService.listSftpUploads(); }
    catch (error) { this.sftpUploadsError = this.getErrorMessage(error, 'Unable to load SFTP uploads.'); }
    finally { this.loadingSftpUploads = false; }
  }

  protected async retrySftpUpload(upload: InboundSftpUpload): Promise<void> {
    if (this.retryingUpload) { return; }
    this.retryingUpload = upload.id;
    try {
      await this.inboundFileFeedsService.retrySftpUpload(upload.id);
      this.snackBar.open('Upload queued for retry.', 'Close', { duration: 3000 });
      await this.loadSftpUploads();
    } catch (error) {
      this.snackBar.open(this.getErrorMessage(error, 'Unable to retry upload.'), 'Close', { duration: 5000 });
    } finally { this.retryingUpload = null; }
  }

  protected async loadFeeds(selectFeedId?: string): Promise<void> {
    this.loadingFeeds = true;
    try {
      const feeds = await this.inboundFileFeedsService.listFeeds(this.filters);
      this.feeds.data = feeds;
      this.feeds.sortingDataAccessor = (feed, property) => this.getFeedSortValue(feed, property);
      this.feeds.sort = this.feedsSort;

      const nextSelectedFeed =
        feeds.find(feed => feed.id === selectFeedId)
        || feeds.find(feed => feed.id === this.selectedFeed?.id)
        || null;

      if (nextSelectedFeed) {
        await this.selectFeed(nextSelectedFeed);
      } else {
        this.selectedFeed = null;
        this.imports.data = [];
        this.errors.data = [];
      }
    } catch (error) {
      console.error('Unable to load inbound feeds', error);
      this.snackBar.open('Unable to load inbound feeds.', 'Close', { duration: 3000 });
    } finally {
      this.loadingFeeds = false;
    }
  }

  protected async loadUnmatchedMessages(selectMessageId?: string): Promise<void> {
    this.loadingUnmatchedMessages = true;
    try {
      const messages = await this.inboundFileFeedsService.listUnmatchedMessages(this.unmatchedMessageFilters);
      this.unmatchedMessages.data = messages;
      this.unmatchedMessages.sortingDataAccessor = (message, property) => this.getUnmatchedMessageSortValue(message, property);
      if (this.unmatchedMessagesSort) {
        this.unmatchedMessages.sort = this.unmatchedMessagesSort;
      }

      const nextSelectedMessageId = selectMessageId || this.getUnmatchedMessageId(this.selectedUnmatchedMessage);
      this.selectedUnmatchedMessage = nextSelectedMessageId
        ? messages.find(message => this.getUnmatchedMessageId(message) === nextSelectedMessageId) || null
        : null;
    } catch (error) {
      console.error('Unable to load unmatched inbound messages', error);
      this.snackBar.open('Unable to load unmatched inbound messages.', 'Close', { duration: 3000 });
    } finally {
      this.loadingUnmatchedMessages = false;
    }
  }

  protected async selectFeed(feed: InboundFileFeed, options: FeedSelectionOptions = {}): Promise<boolean> {
    this.loadingDetails = true;
    this.selectedFeed = feed;
    this.selectedImport = null;
    try {
      const [feedDetails, imports, errors] = await Promise.all([
        this.inboundFileFeedsService.getFeed(feed.id),
        this.inboundFileFeedsService.listFeedImports(feed.id, { status: this.importFilters.status as any, limit: 100, offset: 0 }),
        this.inboundFileFeedsService.listErrors({
          feedId: feed.id,
          status: this.errorFilters.status,
          severity: this.errorFilters.severity,
          limit: 100,
          offset: 0,
        }),
        this.isScopelessHandlerType(feed.handlerType)
          ? Promise.resolve()
          : this.loadScopeOptionsForType(feed.scopeType, '', true),
      ]);
      this.selectedFeed = feedDetails;
      this.imports.data = imports;
      this.imports.sort = this.importsSort;
      this.errors.data = errors;
      this.errors.sort = this.errorsSort;
      this.replaceFeedInTable(feedDetails);
      if (typeof options.selectedTabIndex === 'number') {
        this.selectedTabIndex = options.selectedTabIndex;
      }
      if (options.showToast) {
        this.snackBar.open('Feed selected.', 'Close', { duration: 2500 });
      }
      return true;
    } catch (error) {
      console.error('Unable to load inbound feed detail', error);
      this.snackBar.open('Unable to load inbound feed detail.', 'Close', { duration: 3000 });
      return false;
    } finally {
      this.loadingDetails = false;
    }
  }

  protected browseFiles(feed: InboundFileFeed): void {
    if (this.filesDialog) { return; }
    this.filesFeed = feed;
    this.filesSort = { active: 'name', direction: 'asc' };
    this.cancelFileAction();
    this.filePage = null;
    this.filesError = '';
    this.filesDialog = this.dialog.open(this.filesPanel, {
      width: '800px', maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 48px)',
      ariaLabel: `Browse files — ${feed.name}`, autoFocus: 'first-tabbable', restoreFocus: true,
    });
    this.filesDialog.afterClosed().subscribe(() => {
      this.filesGeneration++;
      this.filesFeed = null;
      this.filePage = null;
      this.filesDialog = null;
      this.filesLoading = false;
    });
    void this.loadFiles();
  }

  protected closeFiles(): void { if (!this.filesBusy) { this.filesDialog?.close(); } }

  protected async openFile(file: InboundSftpFilePage['files'][number], mode: 'view' | 'download'): Promise<void> {
    if (!this.filesFeed || this.filesBusy || this.filesLoading) { return; }
    this.filesError = '';
    // Open synchronously from the user's click so the browser does not block the preview.
    const preview = mode === 'view' ? window.open('about:blank', '_blank') : null;
    if (mode === 'view' && !preview) {
      this.filesError = 'Allow pop-ups for this site to view the file.';
      return;
    }
    if (preview) { preview.opener = null; }
    this.filesBusy = true;
    if (this.filesDialog) { this.filesDialog.disableClose = true; }
    try {
      const { url } = await this.inboundFileFeedsService.sftpFileLink(this.filesFeed.id, file.name, mode);
      if (preview) {
        if (!preview.closed) { preview.location.replace(url); }
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.rel = 'noopener noreferrer';
        link.download = file.name.split('/').pop()!;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      preview?.close();
      this.filesError = this.getErrorMessage(error, 'Unable to open the file. Refresh and try again.');
    } finally {
      this.filesBusy = false;
      if (this.filesDialog) { this.filesDialog.disableClose = false; }
    }
  }

  protected beginFileAction(action: 'move' | 'rename' | 'delete', file: InboundSftpFilePage['files'][number]): void {
    this.fileAction = action;
    this.actionFile = file;
    this.filesError = '';
    this.fileDestination = action === 'rename' ? file.name.split('/').pop()! : '';
  }

  protected cancelFileAction(): void {
    this.fileAction = null;
    this.actionFile = null;
    this.fileDestination = '';
  }

  protected async submitFileAction(): Promise<void> {
    if (!this.filesFeed || !this.actionFile || !this.fileAction || this.filesBusy) { return; }
    const { name, etag } = this.actionFile;
    const action = this.fileAction;
    const value = this.fileDestination.trim();
    if (action === 'rename' && (!value || /[/\\]/.test(value))) {
      this.filesError = 'Enter a filename without folder separators.';
      return;
    }
    const parent = name.includes('/') ? name.slice(0, name.lastIndexOf('/') + 1) : '';
    const destination = action === 'rename' ? parent + value
      : (value ? value.replace(/\/$/, '') + '/' : '') + name.split('/').pop();
    await this.runFileMutation(() => this.inboundFileFeedsService.changeSftpFile(
      this.filesFeed!.id, { action, name, etag, ...(action !== 'delete' ? { destination } : {}) },
    ));
  }

  protected async addFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.filesFeed || this.filesBusy) { return; }
    if (file.size > 25 * 1024 * 1024) {
      this.filesError = 'Choose a file no larger than 25 MB.';
      return;
    }
    await this.runFileMutation(() => this.inboundFileFeedsService.uploadSftpFile(this.filesFeed!.id, file));
  }

  private async runFileMutation(operation: () => Promise<unknown>): Promise<void> {
    this.filesBusy = true;
    this.filesError = '';
    if (this.filesDialog) { this.filesDialog.disableClose = true; }
    try {
      await operation();
      this.cancelFileAction();
      await this.loadFiles();
    } catch (error) {
      this.filesError = this.getErrorMessage(error, 'Unable to update the file. Refresh and try again.');
    } finally {
      this.filesBusy = false;
      if (this.filesDialog) { this.filesDialog.disableClose = false; }
    }
  }

  protected sortFiles(sort: Sort): void {
    this.filesSort = sort;
    this.applyFilesSort();
  }

  private applyFilesSort(): void {
    if (!this.filePage) { return; }
    const { active, direction } = this.filesSort;
    const multiplier = direction === 'desc' ? -1 : 1;
    const compareNames = (a: string, b: string): number => a < b ? -1 : a > b ? 1 : 0;
    this.filePage = { ...this.filePage, files: [...this.filePage.files].sort((a, b) => {
      let comparison: number;
      if (active === 'size') {
        comparison = a.size - b.size;
      } else if (active === 'lastModified') {
        comparison = new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
      } else {
        comparison = compareNames(a.name, b.name);
      }
      return (comparison || compareNames(a.name, b.name)) * multiplier;
    }) };
  }

  protected async loadFiles(more = false): Promise<void> {
    if (!this.filesFeed || this.filesLoading || (more && !this.filePage?.nextToken)) { return; }
    const generation = ++this.filesGeneration;
    const feedId = this.filesFeed.id;
    const token = more ? this.filePage?.nextToken : undefined;
    if (!more) { this.filePage = null; }
    this.filesLoading = true;
    this.filesError = '';
    try {
      const page = await this.inboundFileFeedsService.listSftpFiles(feedId, token || undefined);
      if (generation !== this.filesGeneration) { return; }
      this.filePage = { ...page, files: more ? [...this.filePage!.files, ...page.files] : page.files };
      this.applyFilesSort();
    } catch (error) {
      if (generation === this.filesGeneration) {
        this.filesError = this.getErrorMessage(error, 'Unable to browse SFTP files. Try refreshing.');
      }
    } finally {
      if (generation === this.filesGeneration) { this.filesLoading = false; }
    }
  }

  protected openSftpAccess(feed: InboundFileFeed): void {
    if (this.sftpDialog) { return; }
    this.sftpFeed = feed;
    this.sftpBusy = false;
    this.sftpDialog = this.dialog.open(this.sftpPanel, {
      width: '640px', maxWidth: 'calc(100vw - 48px)', height: 'auto', maxHeight: 'calc(100vh - 48px)',
      ariaLabel: `SFTP access — ${feed.name}`,
      autoFocus: 'first-tabbable', restoreFocus: true,
    });
    this.sftpDialog.afterClosed().subscribe(() => {
      if (this.sftpStatuses[feed.id] === 'checking') { delete this.sftpStatuses[feed.id]; }
      this.sftpFeed = null;
      this.sftpDialog = null;
      this.sftpBusy = false;
    });
  }

  protected closeSftpAccess(): void {
    if (!this.sftpBusy) { this.sftpDialog?.close(); }
  }

  protected onSftpBusy(busy: boolean): void {
    this.sftpBusy = busy;
    if (this.sftpDialog) { this.sftpDialog.disableClose = busy; }
  }

  protected onSftpStatus(event: { feedId: string; status: string }): void {
    this.sftpStatuses[event.feedId] = event.status;
    if (event.status === 'ready' && this.sftpSetupFeed?.id === event.feedId) { this.sftpSetupFeed = null; }
  }

  protected sftpStatusLabel(feed: InboundFileFeed): string {
    if (feed.transportConfig?.sftp?.enabled !== true) { return 'Not configured'; }
    const status = this.sftpStatuses[feed.id];
    if (!status) { return 'Not checked'; }
    if (status === 'ready') { return 'Ready'; }
    if (['not_linked', 'not_configured'].includes(status)) { return 'Not configured'; }
    if (status === 'checking') { return 'Checking…'; }
    return 'Needs attention';
  }

  protected onSftpFeedUpdated(feed: InboundFileFeed): void {
    this.replaceFeedInTable(feed);
    if (this.selectedFeed?.id === feed.id) { this.selectedFeed = feed; }
  }

  protected onFeedSelected(feed: InboundFileFeed): void {
    void this.selectFeed(feed, {
      showToast: true,
    });
  }

  protected showFeedImports(feed: InboundFileFeed): void {
    void this.selectFeed(feed, {
      selectedTabIndex: this.tabIndexes.imports,
      showToast: true,
    });
  }

  protected showFeedErrors(feed: InboundFileFeed): void {
    void this.selectFeed(feed, {
      selectedTabIndex: this.tabIndexes.errors,
      showToast: true,
    });
  }

  protected async loadImports(): Promise<void> {
    if (!this.selectedFeed) {
      return;
    }
    try {
      this.imports.data = await this.inboundFileFeedsService.listFeedImports(this.selectedFeed.id, {
        status: this.importFilters.status as any,
        limit: 100,
        offset: 0,
      });
      this.imports.sort = this.importsSort;
    } catch (error) {
      console.error('Unable to load imports', error);
      this.snackBar.open('Unable to load imports.', 'Close', { duration: 3000 });
    }
  }

  protected async openSftpImport(importId: string): Promise<void> {
    try {
      const item = await this.inboundFileFeedsService.getImport(importId);
      const feed = await this.inboundFileFeedsService.getFeed(item.feedId);
      this.importFilters.status = 'all';
      if (!await this.selectFeed(feed, { selectedTabIndex: this.tabIndexes.imports })) { return; }
      // An older attempt can be outside the feed's most recent 100 imports.
      if (!this.imports.data.some(record => record.id === item.id)) {
        this.imports.data = [item, ...this.imports.data];
      }
      await this.selectImport(item);
    } catch (error) {
      this.snackBar.open('Unable to open the import attempt.', 'Close', { duration: 3000 });
    }
  }

  protected async selectImport(item: InboundFileImport): Promise<void> {
    this.selectedImport = item;
    await this.loadErrors(item.id);
  }

  protected showImportErrors(item: InboundFileImport): void {
    this.selectedTabIndex = this.tabIndexes.errors;
    void this.selectImport(item);
  }

  protected selectUnmatchedMessage(message: InboundFileUnmatchedMessage): void {
    this.selectedUnmatchedMessage = message;
  }

  protected async loadErrors(importId?: string): Promise<void> {
    if (!this.selectedFeed) {
      return;
    }
    try {
      const query: InboundErrorListQuery = {
        feedId: this.selectedFeed.id,
        importId,
        status: this.errorFilters.status,
        severity: this.errorFilters.severity,
        limit: 100,
        offset: 0,
      };
      this.errors.data = importId
        ? await this.inboundFileFeedsService.listImportErrors(importId, query)
        : await this.inboundFileFeedsService.listErrors(query);
      this.errors.sort = this.errorsSort;
    } catch (error) {
      console.error('Unable to load import errors', error);
      this.snackBar.open('Unable to load import errors.', 'Close', { duration: 3000 });
    }
  }

  protected async openCreateFeed(): Promise<void> {
    try {
      await this.loadScopeOptionsForAllTypes(this.scopeSearch);
      this.openFeedDialog(this.getDialogValue(), 'Add');
    } catch (error) {
      console.error('Unable to load scope options for new inbound feed', error);
      this.snackBar.open('Unable to load scope options.', 'Close', { duration: 3000 });
    }
  }

  protected async openEditFeed(feed: InboundFileFeed): Promise<void> {
    try {
      if (!this.isScopelessHandlerType(feed.handlerType)) {
        await this.loadScopeOptionsForType(feed.scopeType, '', true);
      }
      this.openFeedDialog(this.getDialogValue(feed), 'Edit', feed, feed.scopeType);
    } catch (error) {
      console.error('Unable to load scope options for inbound feed edit', error);
      this.snackBar.open('Unable to load scope options.', 'Close', { duration: 3000 });
    }
  }

  protected async archiveFeed(feed: InboundFileFeed): Promise<void> {
    const confirmed = await this.uiAlertService.presentAlertConfirm(`Archive inbound feed ${feed.name}? Existing imports and errors stay available.`);
    if (!confirmed) {
      return;
    }

    try {
      await this.inboundFileFeedsService.archiveFeed(feed.id);
      this.snackBar.open('Inbound feed archived', 'Close', { duration: 3000 });
      await this.loadFeeds();
    } catch (error) {
      console.error('Unable to archive feed', error);
      this.snackBar.open(this.getErrorMessage(error, 'Unable to archive inbound feed.'), 'Close', { duration: 3000 });
    }
  }

  protected async restoreFeed(feed: InboundFileFeed, status: 'active' | 'paused'): Promise<void> {
    try {
      const updatedFeed = await this.inboundFileFeedsService.updateFeed(feed.id, { status } as Partial<InboundFileFeed>);
      this.replaceFeedInTable(updatedFeed);
      this.selectedFeed = updatedFeed;
      this.snackBar.open('Inbound feed restored', 'Close', { duration: 3000 });
    } catch (error) {
      console.error('Unable to restore feed', error);
      this.snackBar.open(this.getErrorMessage(error, 'Unable to restore inbound feed.'), 'Close', { duration: 3000 });
    }
  }

  protected async openImportDetails(item: InboundFileImport): Promise<void> {
    try {
      const importDetails = await this.inboundFileFeedsService.getImport(item.id);
      this.dialog.open(MemberAddeditComponent, {
        data: new MemberAddeditDialogModel({
          ...item,
          ...importDetails,
          resultSummary: importDetails.resultSummary || {},
          metadata: importDetails.metadata || {},
        }, this.getImportDetailFields(), 'Inbound Import', true, 'View'),
      });
    } catch (error) {
      console.error('Unable to load inbound import detail', error);
      this.snackBar.open(this.getErrorMessage(error, 'Unable to load inbound import detail.'), 'Close', { duration: 3000 });
    }
  }

  protected openErrorDetails(error: InboundFileImportError): void {
    this.dialog.open(MemberAddeditComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: new MemberAddeditDialogModel({
        ...error,
        rawRow: error.rawRow || {},
      }, this.getErrorDetailFields(), 'Inbound Import Error', true, 'View'),
    });
  }

  protected openErrorStatusDialog(error: InboundFileImportError, status?: 'resolved' | 'ignored' | 'open'): void {
    const data: ErrorStatusDialogValue = {
      status: status || error.status,
      resolutionNote: error.resolutionNote || '',
    };
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      width: '560px',
      maxWidth: '95vw',
      data: new MemberAddeditDialogModel(data as any, this.getErrorStatusFields(), 'Import Error Status', true, 'Edit'),
    });

    dialogRef.afterClosed().subscribe(async (dialogResult: ErrorStatusDialogValue | null) => {
      if (!dialogResult) {
        return;
      }

      try {
        await this.inboundFileFeedsService.updateError(error.id, {
          status: dialogResult.status as any,
          resolutionNote: dialogResult.resolutionNote || null,
        });
        this.snackBar.open('Import error updated', 'Close', { duration: 3000 });
        await this.loadErrors(this.selectedImport?.id);
      } catch (updateError) {
        console.error('Unable to update import error', updateError);
        this.snackBar.open(this.getErrorMessage(updateError, 'Unable to update import error.'), 'Close', { duration: 3000 });
      }
    });
  }

  protected openUnmatchedMessageStatusDialog(message: InboundFileUnmatchedMessage, status?: 'resolved' | 'ignored' | 'open'): void {
    const data: ErrorStatusDialogValue = {
      status: status || message.status,
      resolutionNote: message.resolutionNote || '',
    };
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      width: '560px',
      maxWidth: '95vw',
      data: new MemberAddeditDialogModel(data as any, this.getUnmatchedMessageStatusFields(), 'Unmatched Message Status', true, 'Edit'),
    });

    dialogRef.afterClosed().subscribe(async (dialogResult: ErrorStatusDialogValue | null) => {
      if (!dialogResult) {
        return;
      }

      const messageId = this.getUnmatchedMessageId(message);
      if (!messageId) {
        this.snackBar.open('Unable to update unmatched message: missing message ID.', 'Close', { duration: 3000 });
        return;
      }

      try {
        await this.inboundFileFeedsService.updateUnmatchedMessage(messageId, {
          status: dialogResult.status as any,
          resolutionNote: dialogResult.resolutionNote || null,
        });
        this.snackBar.open('Unmatched message updated', 'Close', { duration: 3000 });
        await this.loadUnmatchedMessages(messageId);
      } catch (updateError) {
        console.error('Unable to update unmatched message', updateError);
        this.snackBar.open(this.getErrorMessage(updateError, 'Unable to update unmatched message.'), 'Close', { duration: 3000 });
      }
    });
  }

  protected getHandlerLabel(handlerType?: string): string {
    return handlerType ? (HANDLER_LABELS[handlerType] || handlerType) : '';
  }

  protected getScopeLabel(feed?: InboundFileFeed | null): string {
    if (!feed) {
      return '';
    }
    if (this.isScopelessHandlerType(feed.handlerType)) {
      return '';
    }
    return `${SCOPE_LABELS[feed.scopeType] || feed.scopeType}: ${this.getScopeOptionLabel(feed.scopeType, feed.scopeId)}`;
  }

  protected getScopeOptionLabel(scopeType: string, scopeId: string): string {
    const option = (this.scopeOptions as Record<string, InboundFileFeedScopeOption[]>)[scopeType]?.find(item => item.id === scopeId);
    return option ? option.label : scopeId;
  }

  protected getScopeOptionCount(): number {
    const optionsByType = this.scopeOptions as Record<string, InboundFileFeedScopeOption[]>;
    return Object.keys(optionsByType)
      .filter(scopeType => scopeType !== '_meta')
      .reduce((count, scopeType) => {
        const options = optionsByType[scopeType];
        return count + (Array.isArray(options) ? options.length : 0);
      }, 0);
  }

  protected getTransportLabels(feed?: InboundFileFeed | null): string[] {
    if (!feed?.transportConfig) {
      return [];
    }
    return ['email', 'sftp'].filter(transport => !!feed.transportConfig[transport]?.enabled || !!feed.transportConfig[transport]);
  }

  protected setupSftpHost(): string {
    const inboundAddresses = this.capabilities?.inboundAddresses;
    const host = inboundAddresses?.sftpHost || '';
    const port = inboundAddresses?.sftpPort;
    return port ? `${host}:${port}` : host;
  }

  protected getPollStatusLabel(status?: InboundFileFeedTransportPollStatus | null): string {
    return status?.lastRunStatus ? this.toDisplayLabel(status.lastRunStatus) : '';
  }

  protected getPollStatusClass(status?: InboundFileFeedTransportPollStatus | null): string {
    const normalizedStatus = `${status?.lastRunStatus || ''}`.toLowerCase();
    if (normalizedStatus.includes('success')) {
      return 'status-active';
    }
    if (normalizedStatus.includes('processing') || normalizedStatus.includes('running')) {
      return 'status-processing';
    }
    if (normalizedStatus.includes('error') || normalizedStatus.includes('fail')) {
      return 'status-error';
    }
    return 'status-archived';
  }

  protected getPollCountdownLabel(status?: InboundFileFeedTransportPollStatus | null): string {
    const nextPollAtMs = this.parseDateMs(status?.nextPollAt);
    if (nextPollAtMs === null) {
      return 'Schedule unavailable';
    }

    const remainingMs = nextPollAtMs - this.pollStatusNow;
    if (remainingMs <= 0) {
      return 'Poll due now';
    }

    return `In ${this.formatDuration(remainingMs)}`;
  }

  protected getPollTimestampLabel(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }

    return parsedDate.toLocaleString();
  }

  protected getRelativeTimestampLabel(value?: string | null): string {
    const timestampMs = this.parseDateMs(value);
    if (timestampMs === null) {
      return '-';
    }

    const deltaMs = this.pollStatusNow - timestampMs;
    if (deltaMs < 5_000) {
      return 'Just now';
    }
    if (deltaMs < 0) {
      return `In ${this.formatDuration(Math.abs(deltaMs))}`;
    }

    return `${this.formatDuration(deltaMs)} ago`;
  }

  protected getPollDurationLabel(value?: number | null): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '-';
    }

    if (value < 1000) {
      return `${Math.round(value)} ms`;
    }

    const seconds = value / 1000;
    return seconds >= 10 ? `${Math.round(seconds)} s` : `${seconds.toFixed(1)} s`;
  }

  protected getRunnerSummary(status?: InboundFileFeedTransportPollStatus | null): string {
    const parts: string[] = [];
    if (status?.cadenceLabel) {
      parts.push(status.cadenceLabel);
    }
    if (status?.runner?.environment) {
      parts.push(this.toDisplayLabel(status.runner.environment));
    }
    if (status?.runner?.isJobRunner) {
      parts.push('Job runner');
    }
    return parts.join(' · ');
  }

  protected canRunPollNow(): boolean {
    return !this.runningPollNow && this.emailPollStatus?.lastRunStatus !== 'running';
  }

  protected async scanUploadedFiles(): Promise<void> {
    if (this.scanningUploadedFiles) {
      return;
    }
    this.scanningUploadedFiles = true;
    try {
      await this.inboundFileFeedsService.scanUploadedFiles();
      this.snackBar.open('Uploaded-file scan completed. Check import history for processing results.', 'Close', { duration: 5000 });
      await this.loadFeeds(this.selectedFeed?.id);
    } catch (error) {
      this.snackBar.open(this.getErrorMessage(error, 'Unable to scan uploaded files.'), 'Close', { duration: 5000 });
    } finally {
      this.scanningUploadedFiles = false;
    }
  }

  protected async runPollNow(): Promise<void> {
    if (!this.canRunPollNow()) {
      return;
    }

    this.runningPollNow = true;
    try {
      const pollStatus = await this.inboundFileFeedsService.runPollNow();
      this.emailPollStatus = pollStatus?.email || null;
      this.lastCompletedPollKey = this.getCompletedPollKey(this.emailPollStatus);
      this.pollStatusRefreshFailed = false;
      this.pollStatusLoaded = true;
      this.snackBar.open('Inbound email poll completed.', 'Close', { duration: 3000 });
      await this.refreshPollResultViews();
    } catch (error) {
      console.error('Unable to run inbound email poll now', error);
      this.snackBar.open(this.getErrorMessage(error, 'Unable to run inbound email poll.'), 'Close', { duration: 4000 });
      await this.loadPollStatus(false);
    } finally {
      this.runningPollNow = false;
      this.schedulePollStatusRefresh();
    }
  }

  protected formatJson(value: JsonLike | null | undefined): string {
    if (!value) {
      return '{}';
    }
    return JSON.stringify(value, null, 2);
  }

  protected isSelectedFeed(feed: InboundFileFeed): boolean {
    return this.selectedFeed?.id === feed.id;
  }

  protected isSelectedUnmatchedMessage(message: InboundFileUnmatchedMessage): boolean {
    return this.getUnmatchedMessageId(this.selectedUnmatchedMessage) === this.getUnmatchedMessageId(message);
  }

  protected getUnmatchedStatusClass(status?: string | null): string {
    switch (`${status || ''}`.toLowerCase()) {
      case 'resolved':
        return 'status-active';
      case 'ignored':
        return 'status-archived';
      default:
        return 'status-error';
    }
  }

  protected getUnmatchedAttachments(message?: InboundFileUnmatchedMessage | null): any[] {
    const attachments = message?.metadata?.attachments;
    return Array.isArray(attachments) ? attachments : [];
  }

  protected getHandlerFormat(handlerType?: string | null): InboundFileFeedHandlerFormat | null {
    if (!handlerType) {
      return null;
    }
    return this.capabilities?.handlerFormats?.[handlerType] || null;
  }

  protected getHandlerFormatItem(handlerType?: string | null): HandlerFormatViewModel | null {
    if (!handlerType) {
      return null;
    }
    return this.handlerFormatItems.find(item => item.handlerType === handlerType) || null;
  }

  protected getHandlerFormatFields(format?: InboundFileFeedHandlerFormat | null): InboundFileFeedHandlerFormatField[] {
    return format?.fields || [];
  }

  protected getRequiredHandlerFormatFields(format?: InboundFileFeedHandlerFormat | null): InboundFileFeedHandlerFormatField[] {
    return this.getHandlerFormatFields(format).filter(field => !!field.required);
  }

  protected getOptionalHandlerFormatFields(format?: InboundFileFeedHandlerFormat | null): InboundFileFeedHandlerFormatField[] {
    return this.getHandlerFormatFields(format).filter(field => !field.required);
  }

  protected getHandlerFormatLabel(handlerType?: string): string {
    const format = this.getHandlerFormat(handlerType);
    return format?.label || this.getHandlerLabel(handlerType);
  }

  protected getRequiredScopeLabel(format?: InboundFileFeedHandlerFormat | null): string {
    return format?.requiredScopeType ? this.getScopeTypeLabel(format.requiredScopeType) : 'Any supported scope';
  }

  protected getFileTypesLabel(format?: InboundFileFeedHandlerFormat | null): string {
    return format?.fileTypes?.length ? format.fileTypes.join(', ') : 'Not specified';
  }

  protected getHeaderRowLabel(format?: InboundFileFeedHandlerFormat | null): string {
    return format?.headerRow === null || format?.headerRow === undefined ? 'Not applicable' : String(format.headerRow);
  }

  protected getRejectUnknownColumnsLabel(format?: InboundFileFeedHandlerFormat | null): string {
    if (format?.rejectUnknownColumns === undefined || format?.rejectUnknownColumns === null) {
      return 'Not specified';
    }
    return format.rejectUnknownColumns ? 'Yes' : 'No';
  }

  protected getFieldRequiredLabel(field: InboundFileFeedHandlerFormatField): string {
    if (field.requiredValue === false) {
      return field.required ? 'Column required, value optional' : 'No';
    }
    return field.required || field.requiredValue ? 'Yes' : 'No';
  }

  protected getFieldFormatsLabel(field: InboundFileFeedHandlerFormatField): string {
    return field.formats?.length ? field.formats.join(', ') : '-';
  }

  protected getFieldAcceptedValuesLabel(field: InboundFileFeedHandlerFormatField): string {
    return field.acceptedValues?.length ? field.acceptedValues.join(', ') : '-';
  }

  protected canDownloadTemplate(item: HandlerFormatViewModel | null | undefined): boolean {
    return !!item?.downloadTypes.length;
  }

  protected async downloadTemplate(item: HandlerFormatViewModel, fileType: string): Promise<void> {
    const normalizedType = `${fileType || ''}`.toLowerCase();
    if (!item.downloadTypes.includes(normalizedType)) {
      this.snackBar.open(`No ${normalizedType.toUpperCase()} template is available for this handler.`, 'Close', { duration: 3000 });
      return;
    }

    try {
      const headers = item.format.fields?.map(field => field.name) || [];
      const fileName = this.buildTemplateFileName(item.handlerType, normalizedType);

      if (normalizedType === 'csv') {
        this.downloadCsvTemplate(headers, fileName);
      } else if (normalizedType === 'xlsx') {
        await this.downloadXlsxTemplate(headers, fileName);
      } else {
        this.snackBar.open(`Template download is not supported for ${normalizedType.toUpperCase()}.`, 'Close', { duration: 3000 });
        return;
      }
    } catch (error) {
      console.error('Unable to download handler template', error);
      this.snackBar.open('Unable to download handler template.', 'Close', { duration: 3000 });
    }
  }

  private buildHandlerFormatItems(): HandlerFormatViewModel[] {
    const formats = this.capabilities?.handlerFormats || {};
    const orderedHandlerTypes = this.capabilities?.handlerTypes?.length ? this.capabilities.handlerTypes : Object.keys(formats);
    const seen = new Set<string>();
    const items: HandlerFormatViewModel[] = [];

    const pushItem = (handlerType: string): void => {
      const format = formats[handlerType];
      if (!format || seen.has(handlerType)) {
        return;
      }
      seen.add(handlerType);
      const fields = format.fields || [];
      items.push({
        handlerType,
        format,
        requiredFields: fields.filter(field => !!field.required),
        optionalFields: fields.filter(field => !field.required),
        downloadTypes: this.getTemplateDownloadTypes(format, fields),
      });
    };

    orderedHandlerTypes.forEach(pushItem);
    Object.keys(formats).forEach(pushItem);

    return items;
  }

  private getTemplateDownloadTypes(
    format: InboundFileFeedHandlerFormat,
    fields: InboundFileFeedHandlerFormatField[],
  ): string[] {
    if (!fields.length) {
      return [];
    }

    const supportedTypes = new Set(['csv', 'xlsx']);
    return (format.fileTypes || [])
      .map(fileType => `${fileType}`.toLowerCase())
      .filter(fileType => supportedTypes.has(fileType));
  }

  private buildTemplateFileName(handlerType: string, fileType: string): string {
    return `wallit-inbound-${handlerType}-template.${fileType}`;
  }

  private downloadCsvTemplate(headers: string[], fileName: string): void {
    const csvContent = `${headers.map(this.escapeCsvCell).join(',')}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, fileName);
  }

  private async downloadXlsxTemplate(headers: string[], fileName: string): Promise<void> {
    const XLSX = await import('xlsx');
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, fileName);
  }

  private escapeCsvCell(value: string): string {
    const stringValue = `${value ?? ''}`;
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  private openFeedDialog(
    dialogValue: FeedDialogValue,
    titlePrefix: 'Add' | 'Edit',
    originalFeed?: InboundFileFeed,
    allowedScopeType?: InboundFeedScopeType,
  ): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        dialogValue as any,
        this.getFeedFields(dialogValue.scopeOption, allowedScopeType),
        'Inbound Feed',
        true,
        titlePrefix,
        true,
      ),
    });

    dialogRef.afterClosed().subscribe(async (dialogResult: FeedDialogValue | null) => {
      if (!dialogResult) {
        return;
      }

      const payload = await this.getFeedPayload(dialogResult, originalFeed);
      if (!payload) {
        return;
      }

      try {
        const savedFeed = originalFeed
          ? await this.inboundFileFeedsService.updateFeed(originalFeed.id, payload)
          : await this.inboundFileFeedsService.createFeed(payload);
        this.snackBar.open(originalFeed ? 'Inbound feed updated' : 'Inbound feed created', 'Close', { duration: 3000 });
        await this.loadFeeds(savedFeed.id);
        delete this.sftpStatuses[savedFeed.id];
        if (!originalFeed && savedFeed.transportConfig?.sftp?.enabled === true) { this.sftpSetupFeed = savedFeed; }
      } catch (error) {
        console.error('Unable to save inbound feed', error);
        this.snackBar.open(this.getErrorMessage(error, 'Unable to save inbound feed.'), 'Close', { duration: 4000 });
      }
    });
  }

  private async getFeedPayload(dialogResult: FeedDialogValue, originalFeed?: InboundFileFeed): Promise<Partial<InboundFileFeed> | null> {
    const isScopelessHandler = this.isScopelessHandlerType(dialogResult.handlerType);
    const scope = isScopelessHandler ? null : this.parseScopeOption(dialogResult.scopeOption);
    if (!isScopelessHandler && !scope) {
      this.snackBar.open('Scope is required. Update the scope search or paste a known scope option.', 'Close', { duration: 4000 });
      return null;
    }

    const transportConfig = this.getTransportConfig(dialogResult);
    if (!transportConfig.email && !transportConfig.sftp) {
      this.snackBar.open('Enable at least one transport: email or SFTP.', 'Close', { duration: 4000 });
      return null;
    }

    const handlerFormat = this.getHandlerFormat(dialogResult.handlerType);
    if (scope && handlerFormat?.requiredScopeType && handlerFormat.requiredScopeType !== scope.scopeType) {
      this.snackBar.open(
        `${handlerFormat.label || this.getHandlerLabel(dialogResult.handlerType)} feeds require ${this.getRequiredScopeLabel(handlerFormat)} scope.`,
        'Close',
        { duration: 5000 },
      );
      return null;
    }

    if (originalFeed && (
      originalFeed.handlerType !== dialogResult.handlerType
      || (!!scope && (originalFeed.scopeType !== scope.scopeType || originalFeed.scopeId !== scope.scopeId))
    )) {
      const confirmed = await this.uiAlertService.presentAlertConfirm(
        'Changing scope or handler changes how future files are interpreted. Continue?',
      );
      if (!confirmed) {
        return null;
      }
    }

    const validationConfig = this.ensureObject(dialogResult.validationConfig);
    if (dialogResult.handlerType === 'user_brand_migration' && !this.hasAllowedBrandTransitions(validationConfig)) {
      this.snackBar.open(
        'User brand migration feeds require validationConfig.allowedTransitions with at least one fromBrandSlug/toBrandSlug pair.',
        'Close',
        { duration: 5000 },
      );
      return null;
    }

    const payload: Partial<InboundFileFeed> = {
      name: String(dialogResult.name || '').trim(),
      description: dialogResult.description ? String(dialogResult.description).trim() : null,
      status: dialogResult.status as any,
      handlerType: dialogResult.handlerType,
      transportConfig,
      parserConfig: this.ensureObject(dialogResult.parserConfig),
      validationConfig,
      notificationConfig: this.ensureObject(dialogResult.notificationConfig),
      metadata: this.ensureObject(dialogResult.metadata),
    };

    if (scope) {
      payload.scopeType = scope.scopeType as any;
      payload.scopeId = scope.scopeId;
    } else if (isScopelessHandler) {
      payload.scopeType = null as any;
      payload.scopeId = null as any;
    }

    if (dialogResult.feedKey && String(dialogResult.feedKey).trim()) {
      payload.feedKey = String(dialogResult.feedKey).trim();
    }

    return payload;
  }

  private getTransportConfig(dialogResult: FeedDialogValue): Record<string, any> {
    const transportConfig: Record<string, any> = {};
    if (dialogResult.emailEnabled) {
      transportConfig.email = {
        enabled: true,
        allowedSenders: this.splitList(dialogResult.emailAllowedSenders),
        requireAttachment: !!dialogResult.emailRequireAttachment,
        requireDkimPass: !!dialogResult.emailRequireDkimPass,
        requireSpfPass: !!dialogResult.emailRequireSpfPass,
        requirePgp: !!dialogResult.emailRequirePgp,
      };
      const subjectTokens = this.splitList(dialogResult.emailSubjectTokens);
      if (subjectTokens.length) {
        transportConfig.email.subjectTokens = subjectTokens;
      }
    }
    if (dialogResult.sftpEnabled) {
      transportConfig.sftp = {
        enabled: true,
        username: dialogResult.sftpUsername ? String(dialogResult.sftpUsername).trim() : null,
        requirePgp: !!dialogResult.sftpRequirePgp,
        processingMode: dialogResult.sftpProcessingMode === undefined ? 'daily' : dialogResult.sftpProcessingMode,
      };
      if (dialogResult.sftpFolder && String(dialogResult.sftpFolder).trim()) {
        transportConfig.sftp.folder = String(dialogResult.sftpFolder).trim();
      }
    }
    return transportConfig;
  }

  private getDialogValue(feed?: InboundFileFeed, handlerType?: string, scopeType?: InboundFeedScopeType): FeedDialogValue {
    const defaults = this.capabilities?.defaults || {};
    const transportConfig = feed ? (feed.transportConfig || {}) : (defaults.transportConfig || {});
    const email = transportConfig.email || {};
    const sftp = transportConfig.sftp || {};
    const defaultScope = this.getDefaultScopeOption(feed, scopeType);

    return {
      feedKey: feed?.feedKey || '',
      name: feed?.name || '',
      description: feed?.description || '',
      status: feed?.status || defaults.status || 'active',
      scopeOption: defaultScope,
      handlerType: feed?.handlerType || handlerType || this.capabilities?.handlerTypes?.[0] || 'generic_file',
      emailEnabled: !!email.enabled,
      emailSubjectTokens: feed && Array.isArray(email.subjectTokens) ? email.subjectTokens.join(', ') : '',
      emailAllowedSenders: Array.isArray(email.allowedSenders) ? email.allowedSenders.join(', ') : '',
      emailRequireAttachment: email.requireAttachment !== false,
      emailRequireDkimPass: email.requireDkimPass !== false,
      emailRequireSpfPass: email.requireSpfPass !== false,
      emailRequirePgp: !!email.requirePgp,
      sftpEnabled: !!sftp.enabled,
      sftpUsername: sftp.username || '',
      sftpFolder: sftp.folder || '',
      sftpRequirePgp: !!sftp.requirePgp,
      sftpProcessingMode: sftp.processingMode === undefined ? 'daily' : sftp.processingMode,
      parserConfig: this.ensureObject(feed?.parserConfig || defaults.parserConfig || { fileTypes: ['csv', 'xlsx'], headerRow: 1 }),
      validationConfig: this.ensureObject(feed?.validationConfig || defaults.validationConfig || { rejectWholeFileOnError: true }),
      notificationConfig: this.ensureObject(feed?.notificationConfig || defaults.notificationConfig || { sendProcessingReceipt: true, sendErrorReceipt: true }),
      metadata: this.ensureObject(feed?.metadata || {}),
    };
  }

  private getDefaultScopeOption(feed?: InboundFileFeed, scopeType?: InboundFeedScopeType): string {
    if (feed && !this.isScopelessHandlerType(feed.handlerType) && feed.scopeType && feed.scopeId) {
      return this.serializeScopeOption(feed.scopeType, feed.scopeId);
    }
    if (scopeType) {
      const option = this.scopeOptions[scopeType]?.[0];
      return option ? this.serializeScopeOption(scopeType, option.id) : '';
    }
    return '';
  }

  private getFeedFields(currentScopeOption: string, allowedScopeType?: InboundFeedScopeType): Array<Field> {
    return [
      { name: 'feedKey', label: 'Feed key (optional)', fieldType: FieldType.F_STRING, hint: 'Leave blank when creating a feed to let the backend generate a high-entropy key.' },
      { name: 'name', label: 'Name', fieldType: FieldType.F_STRING, required: true },
      { name: 'description', label: 'Description', fieldType: FieldType.F_STRING },
      { name: 'status', label: 'Status', fieldType: FieldType.F_DROPDOWN, values: this.capabilities?.statuses || ['active', 'paused', 'archived'], required: true },
      {
        name: 'scopeOption',
        label: 'Scope',
        fieldType: FieldType.F_DROPDOWN,
        invisible: form => this.isScopelessHandlerType(form.get('handlerType')?.value),
        searchable: true,
        reloadValuesOnSearch: true,
        searchPlaceholder: 'Search scopes...',
        valuesLoader: (form, searchValue) => this.loadScopeDropdownValues(form, currentScopeOption, allowedScopeType, searchValue),
        valuesLoaderDependencies: ['handlerType'],
        required: true,
      },
      { name: 'handlerType', label: 'Handler type', fieldType: FieldType.F_DROPDOWN, values: this.getHandlerDropdownValues(), required: true },
      { name: 'emailEnabled', label: 'Email enabled', fieldType: FieldType.F_TOGGLE, sectionTitle: 'Email configuration' },
      { name: 'emailSubjectTokens', label: 'Email subject tokens', fieldType: FieldType.F_STRING, hint: 'Comma-separated. Leave blank to default to the feed key.' },
      { name: 'emailAllowedSenders', label: 'Allowed senders', fieldType: FieldType.F_STRING, hint: 'Comma-separated. Leave blank to allow any sender.' },
      { name: 'emailRequireAttachment', label: 'Require attachment', fieldType: FieldType.F_TOGGLE },
      { name: 'emailRequireDkimPass', label: 'Require DKIM pass', fieldType: FieldType.F_TOGGLE },
      { name: 'emailRequireSpfPass', label: 'Require SPF pass', fieldType: FieldType.F_TOGGLE },
      { name: 'emailRequirePgp', label: 'Require email PGP', fieldType: FieldType.F_TOGGLE },
      { name: 'sftpEnabled', label: 'SFTP enabled', fieldType: FieldType.F_TOGGLE, sectionTitle: 'SFTP configuration' },
      { name: 'sftpProcessingMode', label: 'SFTP processing', fieldType: FieldType.F_DROPDOWN, required: true,
        values: ['Daily scan:daily', 'On upload completion:upload'],
        hint: 'Upload mode requires configured SFTP access and currently supports vendor-status and generic-file feeds. Processing is asynchronous.' },
      { name: 'sftpUsername', label: 'SFTP username', fieldType: FieldType.F_STRING },
      { name: 'sftpFolder', label: 'SFTP folder', fieldType: FieldType.F_STRING, hint: 'Leave blank to use the backend generated environment/scope/feed key path.' },
      { name: 'sftpRequirePgp', label: 'Require SFTP PGP', fieldType: FieldType.F_TOGGLE },
      { name: 'parserConfig', label: 'Parser config JSON', fieldType: FieldType.F_JSON, required: true, sectionTitle: 'Advanced configuration' },
      { name: 'validationConfig', label: 'Validation config JSON', fieldType: FieldType.F_JSON, required: true, hint: 'User brand migration feeds require allowedTransitions: [{ "fromBrandSlug": "...", "toBrandSlug": "..." }].' },
      { name: 'notificationConfig', label: 'Notification config JSON', fieldType: FieldType.F_JSON, required: true },
      { name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON },
    ];
  }

  private getImportDetailFields(): Array<Field> {
    return [
      { name: 'id', label: 'ID', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'feedId', label: 'Feed ID', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'status', label: 'Status', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'transport', label: 'Transport', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'sourceIdentifier', label: 'Source identifier', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'subject', label: 'Subject', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'originalFileName', label: 'Original file name', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'storageBucket', label: 'Storage bucket', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'storageKey', label: 'Storage key', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'checksum', label: 'Checksum', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'rowCount', label: 'Rows', fieldType: FieldType.F_NUMBER, readOnly: true },
      { name: 'successCount', label: 'Successes', fieldType: FieldType.F_NUMBER, readOnly: true },
      { name: 'errorCount', label: 'Errors', fieldType: FieldType.F_NUMBER, readOnly: true },
      { name: 'warningCount', label: 'Warnings', fieldType: FieldType.F_NUMBER, readOnly: true },
      { name: 'receivedAt', label: 'Received at', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'startedAt', label: 'Started at', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'finishedAt', label: 'Finished at', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'errorSummary', label: 'Error summary', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'resultSummary', label: 'Result summary JSON', fieldType: FieldType.F_JSON, readOnly: true },
      { name: 'metadata', label: 'Metadata JSON', fieldType: FieldType.F_JSON, readOnly: true },
    ];
  }

  private getErrorDetailFields(): Array<Field> {
    return [
      { name: 'id', label: 'ID', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'importId', label: 'Import ID', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'feedId', label: 'Feed ID', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'rowNumber', label: 'Row number', fieldType: FieldType.F_NUMBER, readOnly: true },
      { name: 'fieldName', label: 'Field name', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'errorCode', label: 'Error code', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'severity', label: 'Severity', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'message', label: 'Message', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'status', label: 'Status', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'resolutionNote', label: 'Resolution note', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'createdAt', label: 'Created at', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'updatedAt', label: 'Updated at', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'resolvedAt', label: 'Resolved at', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'resolvedByUserId', label: 'Resolved by user ID', fieldType: FieldType.F_STRING, readOnly: true },
      { name: 'rawRow', label: 'Raw row JSON', fieldType: FieldType.F_JSON, readOnly: true },
    ];
  }

  private getErrorStatusFields(): Array<Field> {
    return [
      { name: 'status', label: 'Status', fieldType: FieldType.F_DROPDOWN, values: this.capabilities?.errorStatuses || ['open', 'resolved', 'ignored'], required: true },
      { name: 'resolutionNote', label: 'Resolution note', fieldType: FieldType.F_STRING },
    ];
  }

  private getUnmatchedMessageStatusFields(): Array<Field> {
    return [
      { name: 'status', label: 'Status', fieldType: FieldType.F_DROPDOWN, values: ['open', 'resolved', 'ignored'], required: true },
      { name: 'resolutionNote', label: 'Resolution note', fieldType: FieldType.F_STRING },
    ];
  }

  private async refreshScopeOptions(preserveOtherTypes = false): Promise<void> {
    this.loadingScopes = true;
    try {
      await this.loadScopeOptionsForType(this.scopeOptionType, this.scopeSearch, preserveOtherTypes);
    } catch (error) {
      console.error('Unable to load scope options', error);
      this.snackBar.open('Unable to load scope options.', 'Close', { duration: 3000 });
    } finally {
      this.loadingScopes = false;
    }
  }

  private async loadScopeOptionsForType(scopeType: InboundFeedScopeType, filter = '', preserveOtherTypes = false): Promise<void> {
    const requestId = ++this.scopeOptionsRequestId;
    const scopedOptions = this.normalizeScopeOptions(await this.inboundFileFeedsService.scopeOptions(scopeType, filter, 250));
    if (requestId !== this.scopeOptionsRequestId) {
      return;
    }
    this.scopeOptions = preserveOtherTypes
      ? {
        ...this.scopeOptions,
        [scopeType]: scopedOptions[scopeType],
      }
      : scopedOptions;
  }

  private async loadScopeOptionsForAllTypes(filter = ''): Promise<void> {
    this.loadingScopes = true;
    const requestId = ++this.scopeOptionsRequestId;
    try {
      const scopeTypes = this.getSupportedScopeTypes();
      const scopedOptionResults = await Promise.all(
        scopeTypes.map(scopeType => this.inboundFileFeedsService.scopeOptions(scopeType, filter, 250))
      );
      if (requestId !== this.scopeOptionsRequestId) {
        return;
      }

      const mergedScopeOptions = this.emptyScopeOptions();
      scopeTypes.forEach((scopeType, index) => {
        const normalizedScopeOptions = this.normalizeScopeOptions(scopedOptionResults[index]);
        mergedScopeOptions[scopeType] = normalizedScopeOptions[scopeType];
      });
      this.scopeOptions = mergedScopeOptions;
    } finally {
      this.loadingScopes = false;
    }
  }

  private getScopeDropdownValues(currentScopeOption: string, allowedScopeType?: InboundFeedScopeType): string[] {
    const values: string[] = [];
    const optionsByType = this.scopeOptions as Record<string, InboundFileFeedScopeOption[]>;
    const scopeTypes = allowedScopeType ? [allowedScopeType] : this.getSupportedScopeTypes();
    scopeTypes.forEach(scopeType => {
      (optionsByType[scopeType] || []).forEach(option => {
        values.push(`${this.getScopeTypeLabel(scopeType)} - ${this.sanitizeDropdownLabel(option.label)}:${this.serializeScopeOption(scopeType, option.id)}`);
      });
    });
    if (currentScopeOption && !values.find(value => value.endsWith(`:${currentScopeOption}`))) {
      const parsed = this.parseScopeOption(currentScopeOption);
      if (parsed) {
        values.unshift(`${this.getScopeTypeLabel(parsed.scopeType)} - ${parsed.scopeId}:${currentScopeOption}`);
      }
    }
    return values;
  }

  private async loadScopeDropdownValues(
    form: any,
    currentScopeOption: string,
    allowedScopeType?: InboundFeedScopeType,
    searchValue = '',
  ): Promise<string[]> {
    const handlerType = form.get('handlerType')?.value;
    if (this.isScopelessHandlerType(handlerType)) {
      return [];
    }

    const requiredScopeType = this.getHandlerFormat(handlerType)?.requiredScopeType || undefined;
    const scopeType = allowedScopeType || requiredScopeType || undefined;
    if (scopeType) {
      await this.loadScopeOptionsForType(scopeType, this.normalizeScopeDropdownSearchValue(searchValue, scopeType), true);
      return this.getScopeDropdownValues(currentScopeOption, scopeType);
    }

    await this.loadScopeOptionsForAllTypes(this.normalizeScopeDropdownSearchValue(searchValue));
    return this.getScopeDropdownValues(currentScopeOption);
  }

  private normalizeScopeDropdownSearchValue(searchValue = '', scopeType?: InboundFeedScopeType): string {
    const normalizedSearchValue = `${searchValue || ''}`.trim();
    if (!normalizedSearchValue) {
      return '';
    }

    const scopeLabels = scopeType
      ? [this.getScopeTypeLabel(scopeType)]
      : this.getSupportedScopeTypes().map(type => this.getScopeTypeLabel(type));
    const matchingLabel = scopeLabels.find(label =>
      normalizedSearchValue.toLowerCase().startsWith(`${label.toLowerCase()} - `)
    );

    return matchingLabel
      ? normalizedSearchValue.slice(`${matchingLabel} - `.length).trim()
      : normalizedSearchValue;
  }

  private sanitizeDropdownLabel(label: string): string {
    return String(label || '').replace(/:/g, ' - ');
  }

  private getHandlerDropdownValues(): string[] {
    return (this.capabilities?.handlerTypes || Object.keys(HANDLER_LABELS)).map(handlerType => {
      const format = this.getHandlerFormat(handlerType);
      const scopeSuffix = !this.isScopelessHandlerType(handlerType) && format ? ` - scope ${this.getRequiredScopeLabel(format)}` : '';
      return `${this.getHandlerFormatLabel(handlerType)}${scopeSuffix}:${handlerType}`;
    });
  }

  private isScopelessHandlerType(handlerType?: string | null): boolean {
    return SCOPELESS_HANDLER_TYPES.has(`${handlerType || ''}`);
  }

  private serializeScopeOption(scopeType: string, scopeId: string): string {
    return `${scopeType}|${scopeId}`;
  }

  private parseScopeOption(value: string): { scopeType: string; scopeId: string } | null {
    const [scopeType, ...scopeIdParts] = String(value || '').split('|');
    const scopeId = scopeIdParts.join('|');
    if (!scopeType || !scopeId) {
      return null;
    }
    return { scopeType, scopeId };
  }

  private normalizeScopeOptions(scopeOptions?: Partial<InboundFileFeedScopeOptions>): InboundFileFeedScopeOptions {
    const normalizedScopeOptions: InboundFileFeedScopeOptions = {
      team: scopeOptions?.team || [],
      company: scopeOptions?.company || [],
      client: scopeOptions?.client || [],
      vendorProduct: scopeOptions?.vendorProduct || [],
      vendor: scopeOptions?.vendor || [],
      marketingGroup: scopeOptions?.marketingGroup || [],
      partner: scopeOptions?.partner || [],
    };

    Object.keys(scopeOptions || {}).forEach(scopeType => {
      const scopeOptionItems = (scopeOptions as Record<string, InboundFileFeedScopeOption[] | any>)[scopeType];
      if (Array.isArray(scopeOptionItems)) {
        (normalizedScopeOptions as Record<string, InboundFileFeedScopeOption[]>)[scopeType] = scopeOptionItems;
      }
    });

    return normalizedScopeOptions;
  }

  private emptyScopeOptions(): InboundFileFeedScopeOptions {
    return {
      team: [],
      company: [],
      client: [],
      vendorProduct: [],
      vendor: [],
      marketingGroup: [],
      partner: [],
    };
  }

  private getSupportedScopeTypes(): InboundFeedScopeType[] {
    return (this.capabilities?.scopeTypes?.length
      ? this.capabilities.scopeTypes
      : Object.keys(SCOPE_LABELS)) as InboundFeedScopeType[];
  }

  private getScopeTypeLabel(scopeType: string): string {
    return SCOPE_LABELS[scopeType] || this.toDisplayLabel(scopeType);
  }

  private splitList(value?: string): string[] {
    return String(value || '')
      .split(',')
      .map(item => item.trim())
      .filter(item => !!item);
  }

  private startPollStatusClock(): void {
    this.pollStatusNow = Date.now();
    if (this.pollStatusTickHandle) {
      clearInterval(this.pollStatusTickHandle);
    }
    this.pollStatusTickHandle = setInterval(() => {
      this.pollStatusNow = Date.now();
    }, 1000);
  }

  private schedulePollStatusRefresh(): void {
    if (this.pollStatusRefreshHandle) {
      clearTimeout(this.pollStatusRefreshHandle);
    }

    this.pollStatusRefreshHandle = setTimeout(() => {
      void this.loadPollStatus(false);
    }, this.getPollStatusRefreshDelay());
  }

  private getPollStatusRefreshDelay(): number {
    if (!this.emailPollStatus) {
      return 60_000;
    }

    const nextPollAtMs = this.parseDateMs(this.emailPollStatus.nextPollAt);
    if (nextPollAtMs === null) {
      return 60_000;
    }

    const nextRefreshMs = nextPollAtMs - Date.now() + 10_000;
    return Math.max(30_000, Math.min(5 * 60_000, nextRefreshMs));
  }

  private parseDateMs(value?: string | null): number | null {
    if (!value) {
      return null;
    }

    const parsedMs = new Date(value).getTime();
    return Number.isNaN(parsedMs) ? null : parsedMs;
  }

  private formatDuration(valueMs: number): string {
    const totalSeconds = Math.max(0, Math.floor(valueMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  private toDisplayLabel(value: string): string {
    return String(value || '')
      .split(/[_\s-]+/)
      .filter(part => !!part)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private ensureObject(value: any): Record<string, any> {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  }

  private hasAllowedBrandTransitions(validationConfig: Record<string, any>): boolean {
    return Array.isArray(validationConfig.allowedTransitions)
      && validationConfig.allowedTransitions.some((transition: any) =>
        !!String(transition?.fromBrandSlug || '').trim()
        && !!String(transition?.toBrandSlug || '').trim()
      );
  }

  private async refreshPollResultViews(): Promise<void> {
    const selectedFeedId = this.selectedFeed?.id;
    const selectedUnmatchedMessageId = this.getUnmatchedMessageId(this.selectedUnmatchedMessage) || undefined;

    await Promise.all([
      this.loadFeeds(selectedFeedId),
      this.loadUnmatchedMessages(selectedUnmatchedMessageId),
    ]);
  }

  private getCompletedPollKey(status?: InboundFileFeedTransportPollStatus | null): string | null {
    if (!status) {
      return null;
    }

    const finishedAt = status.lastFinishedAt || '';
    const runStatus = status.lastRunStatus || '';

    if (!finishedAt || !runStatus) {
      return null;
    }

    return `${finishedAt}|${runStatus}`;
  }

  private replaceFeedInTable(feed: InboundFileFeed): void {
    const items = [...this.feeds.data];
    const index = items.findIndex(item => item.id === feed.id);
    if (index >= 0) {
      items[index] = feed;
      this.feeds.data = items;
    }
  }

  private getFeedSortValue(feed: InboundFileFeed, property: string): string | number {
    switch (property) {
      case 'scope':
        return this.getScopeLabel(feed);
      case 'transports':
        return this.getTransportLabels(feed).join(', ');
      default:
        return String((feed as any)[property] || '');
    }
  }

  private getUnmatchedMessageSortValue(message: InboundFileUnmatchedMessage, property: string): string | number {
    switch (property) {
      case 'createdAt':
        return this.parseDateMs(message.createdAt) || 0;
      default:
        return String((message as any)[property] || '');
    }
  }

  private getUnmatchedMessageId(message?: InboundFileUnmatchedMessage | null): string | null {
    return message?.messageId || message?.id || null;
  }

  private getErrorMessage(error: any, fallback: string): string {
    return error?.error?.error || error?.error?.message || error?.message || fallback;
  }
}
